import type { DatePreset, DateRange, Filters, Season } from '../types/dashboard';

export const DATA_START_YEAR = 2024;
/** Jan 2024 … Sep 2026 */
export const MONTH_COUNT = 33;
export const LATEST_MONTH = MONTH_COUNT - 1;

const SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const LONG = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

export function monthParts(i: number) {
  return { year: DATA_START_YEAR + Math.floor(i / 12), month: ((i % 12) + 12) % 12 };
}

export function monthLabel(i: number): string {
  const { year, month } = monthParts(i);
  return `${SHORT[month]} '${String(year).slice(2)}`;
}

export function monthLong(i: number): string {
  const { year, month } = monthParts(i);
  return `${LONG[month]} ${year}`;
}

export function seasonOf(month: number, year: number): { season: Season; year: number } {
  if (month === 11) return { season: 'Winter', year: year + 1 };
  if (month <= 1) return { season: 'Winter', year };
  if (month <= 4) return { season: 'Spring', year };
  if (month <= 7) return { season: 'Summer', year };
  return { season: 'Fall', year };
}

/** Programs typically start the month after purchase; season/year come from the program, not the purchase date. */
export function programSeasonOfMonth(i: number) {
  const p = monthParts(i + 1);
  return seasonOf(p.month, p.year);
}

const ORDER: Season[] = ['Winter', 'Spring', 'Summer', 'Fall'];

export interface CohortSeason {
  idx: number;
  season: Season;
  year: number;
  label: string;
  short: string;
  startMonth: number;
  inProgress: boolean;
}

export const COHORT_SEASONS: CohortSeason[] = Array.from({ length: 11 }, (_, s) => {
  const ordinal = s + 1;
  const year = DATA_START_YEAR + Math.floor(ordinal / 4);
  const season = ORDER[ordinal % 4];
  return {
    idx: s,
    season,
    year,
    label: `${season} ${year}`,
    short: `${season} '${String(year).slice(2)}`,
    startMonth: 2 + 3 * s,
    inProgress: s === 10,
  };
});

export const DATE_PRESET_LABELS: Record<DatePreset, string> = {
  last13: 'Last 13 months',
  ytd: 'Year to date',
  lastYear: 'Last year',
  custom: 'Custom',
};

export function makeRange(start: number, end: number): DateRange {
  return { start, end, label: start === end ? monthLabel(start) : `${monthLabel(start)} – ${monthLabel(end)}` };
}

export function resolveRange(f: Pick<Filters, 'datePreset' | 'customStart' | 'customEnd'>): DateRange {
  switch (f.datePreset) {
    case 'ytd':
      return makeRange(24, LATEST_MONTH);
    case 'lastYear':
      return makeRange(12, 23);
    case 'custom':
      return makeRange(Math.min(f.customStart, f.customEnd), Math.max(f.customStart, f.customEnd));
    default:
      return makeRange(LATEST_MONTH - 12, LATEST_MONTH);
  }
}

export function shiftRange(r: DateRange, months: number): DateRange | null {
  const start = r.start + months;
  const end = r.end + months;
  if (start < 0 || end > LATEST_MONTH) return null;
  return makeRange(start, end);
}

export function rangeMonths(r: DateRange): number[] {
  return Array.from({ length: r.end - r.start + 1 }, (_, i) => r.start + i);
}
